import { useEffect, useState } from "react";
import Button from "components/button/Button";
import { useTranslation } from "react-i18next";
import {
  useCreateCompanyResponderMutation,
  useCreateRespondingMutation,
  useGetGroupsQuery,
  useGetRespondingByCompanyIdQuery,
  useLazyGetResponderByNationalCodeQuery,
  useUpdateRespondingMutation,
  useDeleteRespondingMutation,
} from "reduxFiles/api/assessmentApi";
import { GroupStatus } from "types/assessmentTypes";
import InputField from "components/fields/InputField";
import { useParams } from "react-router-dom";
import { useGetCompanyQuery } from "reduxFiles/api/companyApi";
import HeaderBox from "components/navbar/HeaderBox";
import { useAppDispatch } from "reduxFiles/hooks";
import { addNotification } from "reduxFiles/slices/notificationSlice";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
  FaUserPlus,
  FaUserCheck,
  FaExclamationTriangle,
  FaToggleOn,
  FaToggleOff,
  FaUser,
  FaUsers,
  FaTrash,
} from "react-icons/fa";
import { useModal } from "hooks/useModal";
import { Modal } from "components/modal";
import ConfirmModal from "components/modal/ConfirmModal";
import { motion, AnimatePresence } from "framer-motion";

/* ---------- Types ---------- */
type ResponderStatus = "ACTIVE" | "DEACTIVE";

interface ResponderItemState {
  respondingId?: number;
  responderId?: number;
  responderName?: string;
  nationalCode?: string;
  mobile?: string;
  status: ResponderStatus;
  isSubmitting?: boolean;
}

interface GroupRowLocalState {
  groupId: number;
  responders: ResponderItemState[];
  newNationalCode: string;
  isAdding: boolean;
}

interface PendingDeleteState {
  groupId: number;
  groupKey: string;
  responder: ResponderItemState;
}

/* ---------- Component ---------- */
const RespondingPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useAppDispatch();

  const confirmModal = useModal();
  const deleteModal = useModal();

  const { data: company, isFetching: isFetchingCompanyData } =
    useGetCompanyQuery({ id: Number(id), scope: "user" }, { skip: !id });

  const [rows, setRows] = useState<GroupRowLocalState[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [pendingGroupId, setPendingGroupId] = useState<number | null>(null);
  const [pendingNationalCode, setPendingNationalCode] = useState<string>("");

  const [pendingDelete, setPendingDelete] = useState<PendingDeleteState | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data: respondingList,
    isFetching: isFetchingRespondingList,
    refetch: refetchRespondingList,
  } = useGetRespondingByCompanyIdQuery(
    { companyId: Number(id), args: { filters: {} } },
    { skip: !id }
  );

  const {
    data: groups = [],
    isFetching: isFetchingGroups,
    refetch: refetchGroups,
  } = useGetGroupsQuery(
    {
      filters: {
        type: company?.totalStaffCount?.companySize || "",
        status: GroupStatus.ACTIVE,
      },
    },
    { skip: !company?.totalStaffCount?.companySize }
  );

  const [getResponder] = useLazyGetResponderByNationalCodeQuery();
  const [createCompanyResponder] = useCreateCompanyResponderMutation();
  const [addRespondingApi] = useCreateRespondingMutation();
  const [updateRespondingApi] = useUpdateRespondingMutation();
  const [deleteRespondingApi] = useDeleteRespondingMutation();

  /* ---------- Refresh ---------- */
  const resetToInitialState = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([refetchRespondingList(), refetchGroups()]);
    } finally {
      setIsRefreshing(false);
    }
  };

  /* ---------- Build rows from API data ---------- */
  useEffect(() => {
    if (!groups.length) {
      setRows([]);
      return;
    }

    setRows((prev) => {
      return groups.map((group) => {
        const prevRow = prev.find((r) => r.groupId === group.id);
        const groupRespondings =
          respondingList?.filter((i) => i.groupId === group?.id) ?? [];

        const responders: ResponderItemState[] = [...groupRespondings]
          .sort((a, b) => {
            if (a.status === "ACTIVE" && b.status !== "ACTIVE") return -1;
            if (a.status !== "ACTIVE" && b.status === "ACTIVE") return 1;
            return (a.id ?? 0) - (b.id ?? 0);
          })
          .map((r) => ({
            respondingId: r.id,
            responderId: r.responder?.id,
            responderName: r.responder?.name || "",
            nationalCode: r.responder?.nationalCode || "",
            mobile: r.responder?.mobile || "",
            status: (r.status as ResponderStatus) || "ACTIVE",
            isSubmitting: false,
          }));

        return {
          groupId: group.id,
          responders,
          newNationalCode: prevRow?.newNationalCode ?? "",
          isAdding: prevRow?.isAdding ?? false,
        };
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, respondingList]);

  /* ---------- Helpers ---------- */
  const updateGroupRow = (
    groupId: number,
    updater: (row: GroupRowLocalState) => GroupRowLocalState
  ) => {
    setRows((prev) =>
      prev.map((row) => (row.groupId === groupId ? updater(row) : row))
    );
  };

  const updateResponderInRow = (
    groupId: number,
    responderId: number | undefined,
    data: Partial<ResponderItemState>
  ) => {
    updateGroupRow(groupId, (row) => ({
      ...row,
      responders: row.responders.map((r) =>
        r.responderId === responderId ? { ...r, ...data } : r
      ),
    }));
  };

  /* ---------- Add new responder flow ---------- */
  const handleCheckAndRegister = async (groupId: number) => {
    const row = rows.find((x) => x.groupId === groupId);
    const nationalCode = row?.newNationalCode?.trim() ?? "";

    if (!nationalCode) {
      dispatch(
        addNotification({
          type: "warning",
          message: t("responding.enterNationalCode"),
        })
      );
      return;
    }

    if (!/^\d{10}$/.test(nationalCode)) {
      dispatch(
        addNotification({
          type: "error",
          message: t("responding.invalidNationalCode"),
        })
      );
      return;
    }

    const alreadyExists = row?.responders.some(
      (r) => r.nationalCode === nationalCode
    );
    if (alreadyExists) {
      dispatch(
        addNotification({
          type: "warning",
          message: t("responding.duplicateResponder"),
        })
      );
      return;
    }

    updateGroupRow(groupId, (r) => ({ ...r, isAdding: true }));

    let openModal = false;

    try {
      try {
        const responderData = await getResponder(nationalCode).unwrap();
        await handleRegisterExistingResponder(
          groupId,
          responderData.responder.id
        );
      } catch (err) {
        const error = err as FetchBaseQueryError;
        if (
          "data" in error &&
          error.data &&
          typeof error.data === "object" &&
          "statusCode" in error.data &&
          (error.data as { statusCode: number }).statusCode === 404
        ) {
          openModal = true;
          setPendingGroupId(groupId);
          setPendingNationalCode(nationalCode);
          confirmModal.openModal();
        } else {
          throw err;
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (!openModal) {
        updateGroupRow(groupId, (r) => ({ ...r, isAdding: false }));
      }
    }
  };

  const handleRegisterExistingResponder = async (
    groupId: number,
    responderId: number
  ) => {
    await addRespondingApi({
      companyId: Number(id),
      items: [{ groupId, responderId }],
    }).unwrap();

    updateGroupRow(groupId, (row) => ({
      ...row,
      newNationalCode: "",
    }));

    await refetchRespondingList();
  };

  /* ---------- Create brand-new responder (modal confirm) ---------- */
  const handleCreateNewResponder = async () => {
    if (!pendingGroupId || !pendingNationalCode) return;

    const groupId = pendingGroupId;
    const nationalCode = pendingNationalCode;

    confirmModal.closeModal();
    updateGroupRow(groupId, (r) => ({ ...r, isAdding: true }));

    try {
      const responderData = await createCompanyResponder({
        nationalCode,
      }).unwrap();

      await handleRegisterExistingResponder(
        groupId,
        responderData.responder.id
      );
    } catch (err) {
      console.error(err);
    } finally {
      setPendingGroupId(null);
      setPendingNationalCode("");
      updateGroupRow(groupId, (r) => ({ ...r, isAdding: false }));
    }
  };

  /* ---------- Toggle status ---------- */
  const handleToggleStatus = async (
    groupId: number,
    responder: ResponderItemState
  ) => {
    if (!responder.respondingId || responder.isSubmitting) return;

    const nextStatus: ResponderStatus =
      responder.status === "ACTIVE" ? "DEACTIVE" : "ACTIVE";

    updateResponderInRow(groupId, responder.responderId, {
      isSubmitting: true,
    });

    try {
      await updateRespondingApi({
        id: responder.respondingId,
        status: nextStatus,
      }).unwrap();

      updateResponderInRow(groupId, responder.responderId, {
        status: nextStatus,
        isSubmitting: false,
      });

      await refetchRespondingList();
    } catch (err) {
      console.error(err);
      updateResponderInRow(groupId, responder.responderId, {
        isSubmitting: false,
      });
    }
  };

  /* ---------- Delete responder (with confirm) ---------- */
  const openDeleteModal = (
    groupId: number,
    groupKey: string,
    responder: ResponderItemState
  ) => {
    if (!responder.respondingId || responder.isSubmitting) return;
    setPendingDelete({ groupId, groupKey, responder });
    deleteModal.openModal();
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;

    const { groupId, responder } = pendingDelete;

    if (!responder.respondingId) {
      setPendingDelete(null);
      deleteModal.closeModal();
      return;
    }

    setIsDeleting(true);
    updateResponderInRow(groupId, responder.responderId, {
      isSubmitting: true,
    });

    try {
      await deleteRespondingApi(responder.respondingId).unwrap();

      updateGroupRow(groupId, (row) => ({
        ...row,
        responders: row.responders.filter(
          (r) => r.responderId !== responder.responderId
        ),
      }));

      await refetchRespondingList();
    } catch (err) {
      console.error(err);
      updateResponderInRow(groupId, responder.responderId, {
        isSubmitting: false,
      });
    } finally {
      setIsDeleting(false);
      setPendingDelete(null);
      deleteModal.closeModal();
    }
  };

  /* ---------- UI helpers ---------- */
  const isLoading =
    isFetchingGroups ||
    isFetchingCompanyData ||
    isFetchingRespondingList ||
    isRefreshing;

  const renderResponderChip = (
    groupId: number,
    groupKey: string,
    responder: ResponderItemState
  ) => {
    const isActive = responder.status === "ACTIVE";
    return (
      <motion.div
        key={responder.respondingId ?? responder.responderId}
        layout
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className={`
          group/chip relative flex items-center gap-3 rounded-xl border p-3 pr-2
          transition-all
          ${
            isActive
              ? "border-green-200 bg-white shadow-sm hover:border-green-300 dark:border-green-800/60 dark:bg-navy-700/50 dark:hover:border-green-700"
              : "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-navy-700/30"
          }
        `}
      >
        <div className="relative shrink-0">
          <div
            className={`
              flex h-10 w-10 items-center justify-center rounded-full
              ${
                isActive
                  ? "bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400"
                  : "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
              }
            `}
          >
            <FaUser size={16} />
          </div>
          <span
            className={`
              absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white dark:border-navy-800
              ${isActive ? "bg-green-500" : "bg-gray-400"}
            `}
          />
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-800 dark:text-white">
            {responder.responderName || t("responding.noname")}
          </p>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
            {responder.nationalCode}
            {responder.mobile ? ` • ${responder.mobile}` : ""}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            disabled={responder.isSubmitting}
            onClick={() => handleToggleStatus(groupId, responder)}
            title={
              isActive ? t("responding.deactivate") : t("responding.activate")
            }
            className={`
              rounded-lg p-1.5 transition-all disabled:opacity-50
              ${
                isActive
                  ? "text-green-600 hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/30"
                  : "text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
              }
            `}
          >
            {isActive ? <FaToggleOn size={20} /> : <FaToggleOff size={20} />}
          </button>

          <button
            type="button"
            disabled={responder.isSubmitting}
            onClick={() => openDeleteModal(groupId, groupKey, responder)}
            title={t("buttons.delete")}
            className="rounded-lg p-1.5 text-red-500 transition-all hover:bg-red-100 disabled:opacity-50 dark:hover:bg-red-900/30"
          >
            <FaTrash size={13} />
          </button>
        </div>
      </motion.div>
    );
  };

  /* ---------- Render ---------- */
  return (
    <div className="mt-5 px-4">
      <HeaderBox
        title={t("company.companyListBack")}
        showBack
        refreshBtn={() => {
          if (!isFetchingGroups && !isFetchingCompanyData && !isRefreshing) {
            resetToInitialState();
          }
        }}
      />

      {isLoading && (
        <div className="py-10 text-center">
          <div className="loader mx-auto w-6 text-brand-500" />
        </div>
      )}

      {!isLoading && !groups.length && (
        <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-gray-700 dark:border-gray-600 dark:text-gray-300">
          {t("responding.noGroupMsg")}
        </div>
      )}

      {!isLoading && groups.length > 0 && (
        <div className="mt-4 flex flex-col gap-4">
          <AnimatePresence>
            {groups.map((group) => {
              const row = rows.find((x) => x.groupId === group?.id);
              if (!row) return null;

              const activeCount = row.responders.filter(
                (r) => r.status === "ACTIVE"
              ).length;
              const totalCount = row.responders.length;

              return (
                <motion.div
                  key={group?.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.25 }}
                  className={`
                    overflow-hidden rounded-2xl border bg-white shadow-sm
                    transition-shadow hover:shadow-md dark:bg-navy-800
                    ${
                      totalCount > 0
                        ? "border-green-200 dark:border-green-800/70"
                        : "border-gray-200 dark:border-gray-700"
                    }
                  `}
                >
                  <div
                    className={`
                      flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4
                      ${
                        totalCount > 0
                          ? "to-transparent border-green-100 bg-gradient-to-l from-green-50/60 dark:border-green-900/40 dark:from-green-900/10"
                          : "border-gray-100 bg-gray-50/60 dark:border-gray-700 dark:bg-navy-700/30"
                      }
                    `}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div
                        className={`
                          flex h-10 w-10 shrink-0 items-center justify-center rounded-xl
                          ${
                            totalCount > 0
                              ? "bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400"
                              : "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                          }
                        `}
                      >
                        <FaUsers size={18} />
                      </div>
                      <h5 className="truncate text-base font-bold text-gray-800 dark:text-white">
                        {t(`assessmentGroups.${group.key}`)}
                      </h5>
                    </div>

                    <span
                      className={`
                        flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold
                        ${
                          totalCount > 0
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                        }
                      `}
                    >
                      <FaUserCheck size={12} />
                      {totalCount > 0
                        ? t("responding.countRegistered", {
                            active: activeCount,
                            total: totalCount,
                          })
                        : t("responding.notRegistered")}
                    </span>
                  </div>

                  <div className="p-5">
                    {row.responders.length > 0 ? (
                      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                        <AnimatePresence>
                          {row.responders.map((r) =>
                            renderResponderChip(group.id, group.key, r)
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <div className="mb-4 rounded-xl border border-dashed border-gray-300 bg-gray-50/50 py-5 text-center dark:border-gray-600 dark:bg-navy-700/30">
                        <FaUser
                          size={20}
                          className="mx-auto mb-2 text-gray-300 dark:text-gray-600"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t("responding.emptyList")}
                        </p>
                      </div>
                    )}

                    {/* Add new responder — compact, right-aligned */}
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-52 shrink-0">
                        <InputField
                          label=""
                          variant=""
                          id={`nationalCode-${group?.id}`}
                          placeholder={t("responder.nationalCode")}
                          value={row.newNationalCode || ""}
                          onChange={(e) => {
                            const value = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 10);
                            updateGroupRow(group.id, (r) => ({
                              ...r,
                              newNationalCode: value,
                            }));
                          }}
                          disabled={row.isAdding}
                        />
                      </div>

                      <Button
                        variant="info"
                        size="sm"
                        className="shrink-0"
                        loading={row.isAdding}
                        disabled={
                          !row.newNationalCode ||
                          row.newNationalCode.length < 10
                        }
                        onClick={() => handleCheckAndRegister(group.id)}
                        startIcon={<FaUserPlus size={14} />}
                      >
                        {t("responding.addResponder")}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* Confirm Modal for Creating New Responder */}
      <Modal isOpen={confirmModal.isOpen} onClose={confirmModal.closeModal}>
        <div className="p-6 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100">
            <FaExclamationTriangle className="h-10 w-10 text-yellow-500" />
          </div>

          <h3 className="mb-2 text-xl font-semibold text-gray-900 dark:text-white">
            {t("responding.confirmCreateTitle")}
          </h3>

          <p className="mb-2 text-gray-700 dark:text-gray-400">
            {t("responding.confirmCreateDesc")}
          </p>

          <p className="mb-6 text-lg font-medium text-brand-600 dark:text-brand-400">
            {t("responding.nationalCodeLabel")}: {pendingNationalCode}
          </p>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                confirmModal.closeModal();
                if (pendingGroupId) {
                  updateGroupRow(pendingGroupId, (r) => ({
                    ...r,
                    isAdding: false,
                  }));
                }
                setPendingGroupId(null);
                setPendingNationalCode("");
              }}
            >
              {t("buttons.cancel")}
            </Button>
            <Button
              variant="info"
              className="flex-1"
              onClick={handleCreateNewResponder}
              startIcon={<FaUserPlus size={18} />}
            >
              {t("responding.confirmCreate")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirm Modal for Deleting Responder */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => {
          setPendingDelete(null);
          deleteModal.closeModal();
        }}
        handler={handleDelete}
        loading={isDeleting}
        title={t("responding.deleteResponder")}
        desc={t("responding.confirmDeleteDesc", {
          name:
            pendingDelete?.responder.responderName || t("responding.noname"),
          group: pendingDelete?.groupKey
            ? t(`assessmentGroups.${pendingDelete.groupKey}`)
            : "",
        })}
      />
    </div>
  );
};

export default RespondingPage;
